/**
 * The price lists a quote can use, in the order the installer prefers: their preferred source
 * first, then the others — the list imported on this device, the wholesalers' lists kept on the
 * server (signed-in accounts, see services/api/src/prices.ts), and Tablo's Belgian reference
 * prices. A line takes its price from the first list that has it.
 */
import type { Project } from '@elec/domain';
import type { Platform, StoredPriceList } from '@elec/platform';
import type { CloudClient, PriceSource } from '@elec/sync';
import type * as Exporters from '@elec/export';

type X = typeof Exporters;

export const OWN_ID = 'own';
export const REFERENCE_ID = 'reference';
/** Preference: the id of the preferred source. */
export const PREFERRED_PREF = 'quotePriceSource';
/** Preference: '0' leaves Tablo's reference prices out. */
export const REFERENCE_PREF = 'quoteReferencePrices';

export interface PriceSourceView {
  id: string;
  name: string;
  kind: 'own' | 'server' | 'reference';
  entries: Exporters.PriceEntry[];
  server?: PriceSource;
}

export interface ServerPrices {
  sources: PriceSource[];
  prices: Record<string, Record<string, { price: number; unit: string; description: string }>>;
}

/** References a quote of this project looks up (devices and cables): what to ask the server. */
export function referencesOf(x: X, project: Project): string[] {
  return [
    ...new Set(
      x
        .buildQuote(project)
        .lines.map((l) => l.reference)
        .filter(Boolean),
    ),
  ];
}

/** Server prices of this project's references; null when not signed in or the server is unreachable. */
export async function loadServerPrices(
  x: X,
  project: Project,
  client: CloudClient | null,
): Promise<{ data: ServerPrices | null; error: boolean }> {
  if (!client) return { data: null, error: false };
  try {
    return { data: await client.priceLookup(referencesOf(x, project)), error: false };
  } catch {
    return { data: null, error: true };
  }
}

/** Every source available, in the default order (own list, wholesalers by name, reference). */
export function availableSources(
  x: X,
  own: StoredPriceList | null,
  server: ServerPrices | null,
  useReference: boolean,
  referenceName: string,
): PriceSourceView[] {
  const out: PriceSourceView[] = [];
  if (own) out.push({ id: OWN_ID, name: own.name, kind: 'own', entries: own.entries });
  for (const s of server?.sources ?? [])
    out.push({
      id: `server:${s.id}`,
      name: s.name,
      kind: 'server',
      server: s,
      entries: Object.entries(server?.prices[s.id] ?? {}).map(([reference, p]) => ({ reference, ...p })),
    });
  if (useReference)
    out.push({ id: REFERENCE_ID, name: referenceName, kind: 'reference', entries: x.BE_PRICES.entries });
  return out;
}

/** The preferred source first (when available), then the others in their order. */
export const ordered = (sources: PriceSourceView[], preferred: string | null) => {
  const first = sources.find((s) => s.id === preferred);
  return first ? [first, ...sources.filter((s) => s !== first)] : sources;
};

export const preferredOf = (platform: Platform) => platform.getPref(PREFERRED_PREF);
export const useReferenceOf = (platform: Platform) => platform.getPref(REFERENCE_PREF) !== '0';
